import { MathLib } from '@morpho-org/blue-sdk'
import { isDefined } from '@morpho-org/morpho-ts'
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils'
import { oneYearInSecs } from '@repo/utils'

/**
 * Formats a BigInt price as a dollar amount with abbreviated notation (k, M, B).
 * @param price - Price value in 18-decimal format
 * @returns Formatted dollar string (e.g., "$1.50k")
 */
export const formatDollars = (price: bigint) => {
  return formatters.short.digits(2).unit('$').of(price, 18)
}

/** A 0.0001 percentage-point step in WAD fraction units, below the two digits we display. */
const FEE_ROUNDING_STEP = 10n ** 12n

/**
 * Formats a WAD-scaled annual fee fraction as a percentage.
 *
 * Management fees are floored on-chain into a per-second rate, so a 2% fee comes back as
 * 0.019999999978704 and the truncating percent formatter would render it as "1.99%". Rounding the
 * floored value back up, at a step below the digits we display, recovers the curator's intended fee
 * without hiding a difference a depositor could see.
 */
export const formatFeePercent = (fee: bigint | null | undefined) => {
  const roundedFee = isDefined(fee)
    ? MathLib.mulDiv(fee, 1, FEE_ROUNDING_STEP, 'Up') * FEE_ROUNDING_STEP
    : fee

  return formatters.percent.trailingZero(false).digits(2).unit('%').default('0%').of(roundedFee, 18)
}

/**
 * Formats a pending `SetManagementFee` value as an annual percentage.
 *
 * That value is the raw contract argument: a per-second rate scaled by WAD (see
 * `accrueInterestView` in the Vault V2 contracts), unlike the current management fee which the API
 * already exposes as an annual fraction.
 */
export const formatPendingManagementFee = (fee: bigint | null | undefined) => {
  return formatFeePercent(isDefined(fee) ? fee * BigInt(oneYearInSecs) : undefined)
}

const apyFormatter = formatters.percent.default('-').unit('%').trailingZero(false).digits(2)

/** Formats an APY given as a plain fraction (e.g. 0.0234 -> "2.34%"). */
export const formatApyPercentage = (apy: number | null | undefined) => apyFormatter.of(apy)

/** Formats a WAD-scaled APY (e.g. 23400000000000000n -> "2.34%"). */
export const formatWadApyPercentage = (apy: bigint | null | undefined) => apyFormatter.of(apy, 18)

const maxApyFormatter = formatters.percent.digits(2).unit('%').default('-').max(10)

const vaultApyFormatter = formatters.percent.digits(2).unit('%').default('-').max(10)

export const formatVaultApy = (apy: number | bigint | null | undefined) =>
  typeof apy === 'number' ? vaultApyFormatter.of(apy) : vaultApyFormatter.of(apy, 18)

/** Formats a Vault V2 max APY given as a plain fraction (e.g. 0.06 -> "6.00%"). */
export const formatMaxApy = (maxApy: number | null | undefined) => maxApyFormatter.of(maxApy)

/**
 * Formats a position LTV (WAD-scaled), capped at 100% for display. A ratio
 * above 1 — an unhealthy position, or the `MAX_UINT_256` "infinite" sentinel
 * `MarketUtils.getLtv` returns when a position has debt but no collateral value
 * (e.g. residual dust debt after withdrawing all collateral) — renders as
 * `> 100%` instead of an unbounded percentage, mirroring the capped APY display.
 */
export const formatLtv = (ltv: bigint | null | undefined, defaultValue = '-') => {
  return formatters.percent
    .digits(2)
    .default(defaultValue)
    .trailingZero(false)
    .unit('%')
    .max(1)
    .of(ltv, 18)
}

/**
 * Rounds an amount up to the precision it will be displayed at.
 *
 * The formatter truncates: it pads the decimal part then slices it, so 0.00396 shown at three
 * digits reads "0.003". Fine for a balance, wrong for a cost — a fee must never render below
 * what the user is charged. Round the value first and the same truncation lands on "0.004".
 */
export const ceilToDisplayedDigits = (
  amount: bigint,
  digits: number,
  decimals: number | undefined
) => {
  const step = 10n ** BigInt(Math.max(0, (decimals ?? 0) - digits))
  if (step <= 1n) return amount
  return ((amount + step - 1n) / step) * step
}

/**
 * `formatAdjustedAmount` fed straight from a token, with the display convention the
 * withdraw surfaces share: no trailing zero, symbol appended. One definition so the
 * convention cannot drift between the surfaces that quote the same amounts.
 */
export const formatTokenAmount = (
  asset: { price?: bigint; decimals?: number; symbol?: string } | null | undefined,
  amount: bigint
) =>
  formatAdjustedAmount({
    price: asset?.price,
    amount,
    decimals: asset?.decimals,
    withTrailingZero: false,
    symbol: asset?.symbol
  })

/**
 * `formatAdjustedAmount` for a fee, rounded up to the displayed precision so the figure is
 * never smaller than what is charged.
 */
export const formatFeeAmount = (params: {
  price?: bigint
  amount: bigint
  decimals: number | undefined
  withTrailingZero: boolean
  symbol?: string
}) => {
  const digits = getDigitsFromPriceAndAmount(params.price, params.amount, params.decimals)
  const step = 10n ** BigInt(Math.max(0, (params.decimals ?? 0) - digits))
  // A positive fee below the display precision must not read as 0 — but promoting it to a
  // whole display unit overstates a one-atomic-unit fee by the entire step (10,000x on a
  // six-decimal dollar token). "<" the smallest displayable amount says both honestly.
  if (params.amount > 0n && params.amount < step) {
    return `<${formatAdjustedAmount({ ...params, amount: step })}`
  }
  return formatAdjustedAmount({
    ...params,
    amount: ceilToDisplayedDigits(params.amount, digits, params.decimals)
  })
}

/**
 * Formats a token amount with intelligent precision based on price and value.
 * @param params.price - Optional USD price per token (18 decimals) for precision calculation
 * @param params.amount - Token amount in native decimals
 * @param params.decimals - Token decimal places (e.g., 18 for ETH, 6 for USDC)
 * @param params.withTrailingZero - Whether to show trailing zeros
 * @param params.symbol - Optional token symbol to append
 * @returns Formatted amount with commas and optional symbol
 */
export const formatAdjustedAmount = ({
  price,
  amount,
  decimals,
  withTrailingZero,
  symbol
}: {
  price?: bigint
  amount: bigint
  decimals: number | undefined
  withTrailingZero: boolean
  symbol?: string
}) => {
  let formatted = formatters.commas
    .digits(getDigitsFromPriceAndAmount(price, amount, decimals))
    .trailingZero(withTrailingZero)

  if (symbol) {
    formatted = formatted.unit(symbol)
  }

  return formatted.of(amount, decimals)
}

/**
 * Get a value, handling undefined cases and optional parsing
 * @param value The value to check
 * @param parser Optional function to parse the value
 * @returns The parsed value if defined, otherwise the original value
 */
export const getParsedValue = <T, R>(
  value: T | undefined | null,
  parser: (val: T) => R
): R | undefined | null => {
  return isDefined(value) ? parser(value) : value
}
